import type {
  DeliveryOrderDto,
  GetDeliveryOrdersResponseDto,
  UpdateOrderStatusDto,
} from '@compassdigital/sdk.typescript/interface/delivery';

/** Delivery order status union — sourced from the delivery SDK's status enum. */
export type DeliveryBoardStatus = UpdateOrderStatusDto['status'];

/**
 * A runner-board order: the delivery SDK's `DeliveryOrderDto`, refining the two
 * fields the swagger generator leaves loose — `status` (`string` → the SDK
 * status union) and `botName` (`object | null` → the robot name it actually is,
 * or null while awaiting a robot).
 */
export type DeliveryBoardOrder = Omit<DeliveryOrderDto, 'status' | 'botName'> & {
  status: DeliveryBoardStatus;
  botName: string | null;
};

/** Per-site board snapshot: the delivery SDK response with refined orders. */
export type DeliveryBoard = Omit<GetDeliveryOrdersResponseDto, 'orders'> & {
  orders: DeliveryBoardOrder[];
};

/**
 * A selectable station in the filter dropdown. Stations are the location brands
 * that are children of a site (`site.locations[].brands[]`), fetched via the
 * public site lookup — not loading areas and not part of the delivery payload.
 */
export interface StationOption {
  id: string;
  name: string;
}

/**
 * A robot is assigned at `accepted` and the entry drops off the board at
 * `transporting` (loaded and gone) — see avride-architecture.md §5.4. These are
 * the statuses a runner still needs to load.
 */
export const LOADABLE_STATUSES: DeliveryBoardStatus[] = ['accepted', 'pickUp', 'pickUpTransaction'];

/** Runner-facing labels for the statuses shown on the board. */
export const STATUS_LABELS: Partial<Record<DeliveryBoardStatus, string>> = {
  new: 'Awaiting robot',
  accepted: 'Assigned',
  pickUp: 'Ready to load',
  pickUpTransaction: 'Loading',
};
